/**
 * What each part of a freshly loaded piece should be made of, read from the
 * names the FILE gives its parts.
 *
 * Two kinds of name, two kinds of answer:
 *
 *  - **iJewel's default layer names** — `Metal`, `Metal 01` … `Metal 09`,
 *    `Gem`, `Gem 01` … `Gem 09`. A file exported for iJewel names its layers
 *    this way on purpose, and iJewel tags every one of them with the same two
 *    materials (`1-metal-whitegold-polished.pmat`, `1-gem-diamond-white-1.dmat`).
 *    These are MATCHED: white gold, polished / a clear diamond, no question.
 *  - **Anything else** — "18K_Rose", "PT950 shank", "CZ pave", "Shank_A". CAD
 *    files often carry the jeweller's intent in a name, so the word matcher
 *    below reads it back as a best guess ("18K_Rose" → rose gold). A name that
 *    says nothing keeps the viewer's own metal-or-stone call. Either way the
 *    part is NOT matched: the dialog asks, pre-filled with the guess.
 *
 * Pure data in, data out — no RPC — so it is checked without a browser
 * (`scripts/check-material-matcher.ts`).
 *
 * History: this used to be `suggestMaterials`, which spoke only when a name
 * DISAGREED with how the part already looked, so a model with generic names
 * ("METAL01", "Gem 01") got nothing at all. The dialog that replaced it asks
 * about every part of every new piece, so every part gets a proposal here.
 */

export type PartKind = "metal" | "gem";

export interface PartFacts {
  id: string;
  /** The file's own name for the part (`GroupInfo.label`). */
  label: string;
  /**
   * A second name from the FILE, when one is known (a CAD layer name).
   * ⚠️ Never `GroupInfo.materialLabel`: that is the viewer's label for the
   * material it assigned itself, not anything the file said.
   */
  materialLabel?: string;
  /** How the viewer classified the part on load. */
  kind: PartKind;
}

/** Preset ids the running viewer actually has (`Catalogs`); unknown ids are never proposed. */
export interface MatcherCatalog {
  metalIds: readonly string[];
  gemIds: readonly string[];
  finishIds: readonly string[];
}

export interface MaterialProposal {
  groupId: string;
  kind: PartKind;
  presetId: string;
  /** Metals only. */
  finishId?: string;
  /** The name is one of iJewel's default layer names, so there is nothing to ask. */
  matched: boolean;
  /** The name that decided the proposal, as the file wrote it. Absent when the name said nothing. */
  because?: string;
}

/**
 * iJewel's defaults — `whiteGold` + `polished` and `diamondClear` are the
 * viewer's ports of `1-metal-whitegold-polished.pmat` and
 * `1-gem-diamond-white-1.dmat` (webgl-jewelry-viewer `metalPresets.ts`,
 * `DiamondCubemap.ts` DIAMOND_PRESETS). They are also what the viewer itself
 * puts on a first load.
 */
export const DEFAULT_METAL = "whiteGold";
export const DEFAULT_FINISH = "polished";
export const DEFAULT_GEM = "diamondClear";

/**
 * `Metal`, `Metal 01`, `metal_3`, `GEM-09`, `Gem1`: the word, then optionally
 * one separator and a number 1–9 that may carry ONE leading zero. Anchored at
 * both ends, so `Metal 10`, `Metallic`, `Gemstone` and `Metal 0` are not
 * default layers — they go to the word matcher like any other name.
 */
const DEFAULT_LAYER = /^(metal|gem)(?:[ _-]?0?[1-9])?$/i;

/** The kind a default layer name stands for, or null when the name is not one. */
export function defaultLayerKind(name: string): PartKind | null {
  const m = DEFAULT_LAYER.exec(name.trim());
  if (!m) return null;
  return m[1].toLowerCase() === "metal" ? "metal" : "gem";
}

/** `want` when the viewer has it; otherwise its first preset (an id it can render beats one it cannot). */
function available(want: string, ids: readonly string[]): string {
  return ids.includes(want) ? want : ids[0] ?? want;
}

/** What a part of `kind` becomes when nothing more specific is known. */
export function defaultPreset(kind: PartKind, cat: MatcherCatalog): { presetId: string; finishId?: string } {
  return kind === "metal"
    ? { presetId: available(DEFAULT_METAL, cat.metalIds), finishId: available(DEFAULT_FINISH, cat.finishIds) }
    : { presetId: available(DEFAULT_GEM, cat.gemIds) };
}

// ── the word matcher ──────────────────────────────────────────────────────

interface Rule {
  kind: PartKind;
  presetId: string;
  /** Whole-token matches (the name split on separators and camel case). */
  tokens: string[];
  /** Substring matches inside the squashed name, for "rosegold", "pt950". */
  fragments?: string[];
}

// Order matters inside a kind: the first rule that matches wins, so the
// specific colours sit before bare "gold".
const METAL_RULES: Rule[] = [
  { kind: "metal", presetId: "roseGold", tokens: ["rose", "rg"], fragments: ["rosegold", "redgold", "pinkgold"] },
  { kind: "metal", presetId: "whiteGold", tokens: ["wg"], fragments: ["whitegold", "weissgold"] },
  { kind: "metal", presetId: "yellowGold", tokens: ["yellow", "yg", "gelb", "750y"], fragments: ["yellowgold", "gelbgold"] },
  { kind: "metal", presetId: "platinum", tokens: ["platinum", "plat", "pt", "pt950", "pt900", "platin"], fragments: ["platinum"] },
  { kind: "metal", presetId: "silver", tokens: ["silver", "sterling", "925", "ag", "argent"], fragments: ["silver", "sterling"] },
  // Bare "gold" with no colour word is yellow gold by trade convention.
  { kind: "metal", presetId: "yellowGold", tokens: ["gold", "10k", "14k", "18k", "22k", "750", "585"] },
];

const GEM_RULES: Rule[] = [
  { kind: "gem", presetId: "ruby", tokens: ["ruby", "rubin"] },
  { kind: "gem", presetId: "sapphire", tokens: ["sapphire", "sapph", "saphir"], fragments: ["sapphire"] },
  { kind: "gem", presetId: "emerald", tokens: ["emerald", "smaragd"] },
  // Stones we have no dedicated look for render as a diamond, which is what
  // they are meant to resemble.
  {
    kind: "gem", presetId: "diamondClear",
    tokens: ["diamond", "diamonds", "dia", "brilliant", "brill", "moissanite", "moiss", "cz", "zirconia", "melee"],
    fragments: ["diamond", "moissanite", "zirconia"],
  },
];

// A lone "white" is white gold only when the name is otherwise about metal —
// "white diamond" and "white sapphire" are stones.
const WHITE_GOLD_CONTEXT = ["gold", "au", "10k", "14k", "18k", "750", "585"];

// Ids are the VIEWER's finish ids. Its brushed finish is "brushed" — the
// reference calls it "brush", and proposing that id meant a viewer that does
// not have it, so a "satin" part never got its finish.
const FINISH_RULES: Array<{ id: string; tokens: string[] }> = [
  { id: "hammered", tokens: ["hammered", "hammer", "hammertone"] },
  { id: "brushed", tokens: ["brushed", "brush", "satin"] },
  { id: "sand", tokens: ["sand", "sandblast", "sandblasted", "matte", "matt", "frosted"] },
];

/** "18K_RoseGold-Band.001" → ["18k", "rose", "gold", "band", "001"]. */
export function tokenize(name: string): string[] {
  return name
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function squash(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function firstMatch(rules: Rule[], tokens: string[], squashed: string): { rule: Rule; hit: string } | null {
  for (const rule of rules) {
    const tok = rule.tokens.find((t) => tokens.includes(t));
    if (tok) return { rule, hit: tok };
    const frag = rule.fragments?.find((f) => squashed.includes(f));
    if (frag) return { rule, hit: frag };
  }
  return null;
}

function metalMatch(tokens: string[], squashed: string): { rule: Rule; hit: string } | null {
  const bareGold = METAL_RULES[METAL_RULES.length - 1];
  const m = firstMatch(METAL_RULES.slice(0, -1), tokens, squashed);
  if (m) return m;
  // Before bare "gold": "white 18k gold" is white gold, not yellow.
  if (tokens.includes("white") && tokens.some((t) => WHITE_GOLD_CONTEXT.includes(t))) {
    return { rule: METAL_RULES.find((r) => r.presetId === "whiteGold")!, hit: "white" };
  }
  return firstMatch([bareGold], tokens, squashed);
}

/** Whichever of the part's names carried the match, as the user wrote it. */
function whichName(part: PartFacts, hit: string): string {
  for (const n of [part.label, part.materialLabel]) {
    if (!n) continue;
    if (tokenize(n).includes(hit) || squash(n).includes(hit)) return n;
  }
  return part.label;
}

/** The word matcher's best guess for a part whose name is not a default layer. */
function guessFromName(part: PartFacts, cat: MatcherCatalog): Omit<MaterialProposal, "groupId" | "matched"> {
  const name = [part.label, part.materialLabel].filter(Boolean).join(" ");
  const tokens = tokenize(name);
  const squashed = squash(name);
  const metal = metalMatch(tokens, squashed);
  const gem = firstMatch(GEM_RULES, tokens, squashed);
  // Both kinds named ("Diamond setting 18K"): the viewer's own
  // classification breaks the tie — we refine it, we don't overrule it.
  const pick = metal && gem ? (part.kind === "gem" ? gem : metal) : metal ?? gem;

  // The name decides the KIND even when the viewer lacks the exact preset it
  // names ("Rose gold" on a viewer without rose gold is still metal).
  const kind = pick?.rule.kind ?? part.kind;
  const fallback = defaultPreset(kind, cat);
  const known = !!pick && (kind === "metal" ? cat.metalIds : cat.gemIds).includes(pick.rule.presetId);
  const finish =
    kind === "metal" ? FINISH_RULES.find((f) => cat.finishIds.includes(f.id) && f.tokens.some((t) => tokens.includes(t))) : undefined;
  const hit = pick?.hit ?? finish?.tokens.find((t) => tokens.includes(t));

  return {
    kind,
    presetId: known ? pick!.rule.presetId : fallback.presetId,
    ...(kind === "metal" ? { finishId: finish?.id ?? fallback.finishId } : {}),
    ...(hit ? { because: whichName(part, hit) } : {}),
  };
}

// ── the kind a name states ────────────────────────────────────────────────

/**
 * Words that say WHICH KIND a part is without naming a material: "Stone_3",
 * "Gemstones", "Shank", "Metal_Band". The viewer's own load-time classifier
 * reads the same stone words (`autoClassify`: diamond|gem|stone|ruby|
 * sapphire|emerald|crystal).
 */
const GEM_WORDS = ["gem", "gems", "gemstone", "gemstones", "stone", "stones", "crystal", "crystals"];
const METAL_WORDS = ["metal", "metals", "shank", "band", "prong", "prongs", "bezel"];

/**
 * The kind a part's NAME says it is, or null when the name says nothing — or
 * says both ("Diamond setting 18K", "Stone_Prongs"), where the name is no
 * evidence either way.
 *
 * iJewel's default layer names decide first (`Gem 01` is a stone, `Metal 01`
 * metal), then any stone or metal word: "diamond", "Stone_3", "CZ pave" are
 * stones; "18K_Rose", "PT950", "Shank" are metal.
 *
 * Used by the customer configurator to keep a part's kind STABLE: what the
 * viewer reports right now can be wrong (a pearl or a metal file put on the
 * stones re-files them as metal), the file's name cannot be.
 */
export function kindFromName(name: string): PartKind | null {
  const raw = name.trim();
  if (!raw) return null;
  const layer = defaultLayerKind(raw);
  if (layer) return layer;
  // "Metal10" / "Gem12" / "PT950" split at the letter→digit step; "18K" stays whole.
  const tokens = tokenize(raw.replace(/([A-Za-z])(\d)/g, "$1 $2"));
  const squashed = squash(raw);
  const gem = !!firstMatch(GEM_RULES, tokens, squashed) || tokens.some((t) => GEM_WORDS.includes(t));
  const metal = !!metalMatch(tokens, squashed) || tokens.some((t) => METAL_WORDS.includes(t));
  if (gem === metal) return null;
  return gem ? "gem" : "metal";
}

/**
 * A proposal for EVERY part, in the order given: matched for iJewel's default
 * layer names, a best guess (`matched: false`) for everything else.
 */
export function defaultMapping(parts: readonly PartFacts[], cat: MatcherCatalog): MaterialProposal[] {
  return parts.map((part) => {
    const layer = defaultLayerKind(part.label);
    if (layer) {
      return { groupId: part.id, kind: layer, ...defaultPreset(layer, cat), matched: true, because: part.label.trim() };
    }
    return { groupId: part.id, matched: false, ...guessFromName(part, cat) };
  });
}
